/* ==================================================
  c_sq-location-display-name.js — SQ ロケーションの「表示名」（Phil の表示名）取得
  - 店舗名を Phil の「名前」ではなく「表示名」で出すために使う（TABIO-1408）。
    使う画面: 店舗在庫確認モーダル（snippets/c_sq-store-inventory.liquid）、受取店舗の選択画面
    （assets/c_page-payment-form.js）、マイページのお気に入り店舗（sections/c_mypage_favorite-store.liquid）。
  - SQ SDK 0.0.42 の getLocations / getLocationAvailabilities は displayName を返さない。そこで SDK と
    同じ App Proxy /apps/sq-api に id と displayName だけを問い合わせ、location.id で引く表を作る。
  - 取得できない（HTTP エラー・GraphQL エラー・タイムアウト）時は空の表で resolve する（reject しない）。
    表示名を引けない店舗は従来どおり「名前」を出すので、店舗一覧の表示そのものは止めない。
  - 同じ 1 回の問い合わせで RETAIL の location.id も集める（loadLocations）。店舗在庫確認モーダルは
    これで倉庫（WAREHOUSE）を外す（HDN1794-1938）。こちらは失敗時に reject する。
  - ビルド工程なしの素の IIFE。window.CSqLocationDisplayName にぶら下げる（import/export しない）。
===================================================*/
(function () {
  'use strict';

  const LOG_PREFIX = '[c_sq-location-display-name]';

  /* SDK の getLocations(…, { locationType: 'RETAIL' }) と同じ母集合。filter を外すと SQ が 500 を返す */
  const QUERY =
    'query CLocationDisplayNames($first: Int, $after: String) {' +
    ' locations(first: $first, after: $after, filter: { locationType: RETAIL }) {' +
    ' nodes { id displayName } pageInfo { hasNextPage endCursor } } }';

  /* 各画面の fetchAllLocations と同じく 1000 件で 1 リクエストに収め、hasNextPage のループは安全網 */
  const PAGE_SIZE = 1000;
  const MAX_PAGES = 50;

  /* nodes の表示名を map（location.id → 表示名）に足す。
     Phil には末尾スペース付きの表示名があるので前後の空白は落とす。空の表示名は入れない（名前に倒す）。 */
  function addDisplayNames(map, nodes) {
    (nodes || []).forEach(function (node) {
      if (!node || !node.id) return;
      const displayName = typeof node.displayName === 'string' ? node.displayName.trim() : '';
      if (displayName) map.set(String(node.id), displayName);
    });
    return map;
  }

  /* nodes の location.id を RETAIL の id 集合に足す。表示名が空の店舗も入れる（表示名の表とは別） */
  function addRetailIds(ids, nodes) {
    (nodes || []).forEach(function (node) {
      if (node && node.id) ids.add(String(node.id));
    });
    return ids;
  }

  /* 画面に出す店舗名。表示名を引ければ表示名、引けなければ SQ の名前 */
  function pickStoreName(location, displayNames) {
    if (!location) return '';
    const id = location.id ? String(location.id) : '';
    const displayName = id && displayNames ? displayNames.get(id) : '';
    return displayName || location.name || '';
  }

  /* 表示名の表と RETAIL の id 集合をページで 1 回だけ取得する loader。
     - load() は表示名の表、loadLocations() は { displayNames, retailIds } を返す。両方で 1 回の取得を共有する
       （モーダルの開き直し・バリアント切替のたびに取り直さない）。
     - 失敗時、load() は空の表で resolve、loadLocations() は reject。どちらも共有 promise を破棄する
       （次の呼び出しで取り直す）。
     - タイムアウトは全ページ・res.json() まで含めて timeoutMs で打ち切り、通信も abort する。
     fetchImpl / setTimeoutImpl / clearTimeoutImpl はテスト用の差し替え口（省略時はブラウザ標準）。 */
  function createDisplayNameLoader(options) {
    const url = options.url;
    const timeoutMs = options.timeoutMs;
    const fetchImpl = options.fetchImpl || function (u, o) { return window.fetch(u, o); };
    const setTimeoutImpl = options.setTimeoutImpl || function (fn, ms) { return setTimeout(fn, ms); };
    const clearTimeoutImpl = options.clearTimeoutImpl || function (id) { clearTimeout(id); };

    let shared = null;

    async function fetchAll(signal) {
      const map = new Map();
      const retailIds = new Set();
      let after = null;
      let pages = 0;
      do {
        const res = await fetchImpl(url, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-SQ-CLIENT': 'ShopifyAppProxy',
          },
          body: JSON.stringify({ query: QUERY, variables: { first: PAGE_SIZE, after: after } }),
          signal: signal,
        });
        if (!res.ok) throw new Error('sq-api: HTTP ' + res.status);
        const json = await res.json();
        const connection = json && json.data && json.data.locations;
        if (!connection) throw new Error('sq-api: locations missing ' + JSON.stringify((json && json.errors) || null));
        addDisplayNames(map, connection.nodes);
        addRetailIds(retailIds, connection.nodes);
        const info = connection.pageInfo || {};
        after = info.hasNextPage ? info.endCursor : null;
        pages += 1;
      } while (after && pages < MAX_PAGES);
      return { displayNames: map, retailIds: retailIds };
    }

    function fetchOnce() {
      const controller = typeof AbortController === 'function' ? new AbortController() : null;
      let timer = null;
      const timeout = new Promise(function (_, reject) {
        timer = setTimeoutImpl(function () {
          if (controller) controller.abort();
          reject(new Error('sq-api: timeout after ' + timeoutMs + 'ms'));
        }, timeoutMs);
      });
      const work = fetchAll(controller ? controller.signal : undefined);
      /* タイムアウトが先に決着した後の abort による reject を握りつぶす */
      work.catch(function () {});
      return Promise.race([work, timeout]).finally(function () { clearTimeoutImpl(timer); });
    }

    function loadLocations() {
      if (shared) return shared;
      const p = fetchOnce();
      shared = p;
      /* 失敗は共有しない（次の呼び出しで取り直す）。誰も await しなくても unhandledRejection にしない */
      p.catch(function () {
        if (shared === p) shared = null;
      });
      return p;
    }

    function load() {
      return loadLocations().then(function (result) {
        return result.displayNames;
      }, function (err) {
        console.warn(LOG_PREFIX + ' 表示名を取得できないため名前で表示します', err);
        return new Map();
      });
    }

    return { load: load, loadLocations: loadLocations };
  }

  /* 画面から使う既定の loader。URL は SDK と同じ（request.origin + /apps/sq-api） */
  let defaultLoader = null;
  function getDefaultLoader() {
    if (!defaultLoader) {
      defaultLoader = createDisplayNameLoader({
        url: window.location.origin + '/apps/sq-api',
        timeoutMs: 10000,
      });
    }
    return defaultLoader;
  }

  function load() {
    return getDefaultLoader().load();
  }

  function loadLocations() {
    return getDefaultLoader().loadLocations();
  }

  window.CSqLocationDisplayName = {
    addDisplayNames: addDisplayNames,
    addRetailIds: addRetailIds,
    pickStoreName: pickStoreName,
    createDisplayNameLoader: createDisplayNameLoader,
    load: load,
    loadLocations: loadLocations,
  };
})();
